import { useState, useEffect, useMemo } from 'react';
import { MdPayment, MdReceipt, MdAdd, MdCalendarMonth, MdEdit, MdDelete, MdCheckCircle, MdSchedule } from 'react-icons/md';
import api from '../../services/api';
import { t } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';

const MONTH_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'];

function normalizeDateToYmd(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes('T')) return s.split('T')[0];
  const dotted = s.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (dotted) {
    const day = dotted[1].padStart(2, '0');
    const month = dotted[2].padStart(2, '0');
    return `${dotted[3]}-${month}-${day}`;
  }
  return s;
}

function timeToMinutes(time) {
  if (!time) return 0;
  const [hours, minutes] = String(time).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

function getDurationHours(startTime, endTime) {
  return (timeToMinutes(endTime) - timeToMinutes(startTime)) / 60;
}

function getEffectiveCalendarOwnerIdForPayments(friendsList) {
  if (!Array.isArray(friendsList)) return null;
  const incomingShared = friendsList.filter((f) => f?.isShared && f?.sharedByUserId);
  if (incomingShared.length === 0) return null;
  const withCalendar = incomingShared.find(
    (f) => f.calendarShared || f.shareSettings?.calendar
  );
  return (withCalendar || incomingShared[0]).sharedByUserId;
}

function formatApiError(error) {
  const data = error?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map((e) => e.msg || e.message).filter(Boolean).join(', ');
  }
  return error?.message || '';
}

export default function PaymentsPage() {
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [friends, setFriends] = useState([]);
  const [manualPaymentDates, setManualPaymentDates] = useState([]); // [{ date, time }]
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [monthsToShow, setMonthsToShow] = useState(12); // Show last 12 months initially
  const [unpaidDebt, setUnpaidDebt] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, paymentId: null });
  const [paymentsLoadError, setPaymentsLoadError] = useState(false);
  const [markAsPaidTarget, setMarkAsPaidTarget] = useState(null); // { month, dates, totalDebt } za "Prvi deo dospeva"
  const { showToast, removeToast, toasts } = useToast();
  const effectiveOwnerId = useMemo(
    () => getEffectiveCalendarOwnerIdForPayments(friends),
    [friends]
  );
  const sharedParams = useMemo(
    () => (effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {}),
    [effectiveOwnerId]
  );
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    method: 'bank_transfer',
    description: '',
    status: 'pending',
    periodStartDate: '',
    periodEndDate: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Refetch manual payment dates when page/tab gets focus (e.g. after setting date on calendar)
  useEffect(() => {
    const onFocus = () => {
      api.get('/calendar-entries/manual-payment-dates', { params: sharedParams })
        .then((res) => {
          const raw = res?.data?.manualPaymentDateTimes || res?.data?.manualPaymentDates || [];
          const normalized = (raw || []).map((item) => {
            if (!item) return null;
            if (typeof item === 'string') return { date: item.split('T')[0], time: '14:00' };
            const rawDate = item.date || item.paymentDate || item.payment_date;
            if (!rawDate) return null;
            const date = typeof rawDate === 'string' ? rawDate.split('T')[0] : rawDate instanceof Date ? rawDate.toISOString().split('T')[0] : null;
            if (!date) return null;
            const rawTime = item.time || item.paymentTime || item.payment_time || '14:00';
            const time = typeof rawTime === 'string' && /^\d{2}:\d{2}/.test(rawTime) ? rawTime.slice(0, 5) : '14:00';
            return { date, time };
          }).filter(Boolean);
          setManualPaymentDates(normalized);
        })
        .catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sharedParams]);

  const loadData = async () => {
    try {
      const friendsResponse = await api.get('/friends').catch(() => ({ data: [] }));
      const friendsList = Array.isArray(friendsResponse.data) ? friendsResponse.data : [];
      setFriends(friendsList);
      const ownerForView = getEffectiveCalendarOwnerIdForPayments(friendsList);
      const requestParams = ownerForView ? { viewAsUserId: ownerForView } : {};

      const now = new Date();
      const monthsToFetch = [];
      for (let i = 0; i < 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        monthsToFetch.push({ month: d.getMonth() + 1, year: d.getFullYear() });
      }
      const [
        jobsResponse,
        paymentsResponse,
        unpaidDebtResponse,
        manualDatesResponse,
        ...calendarMonths
      ] = await Promise.all([
        api.get('/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/payments', { params: requestParams }).catch((err) => {
          console.error('Error loading payments:', err);
          return { data: [], _paymentsError: true };
        }),
        api.get('/payments/unpaid-debt', { params: requestParams }).catch((err) => {
          console.error('Error loading unpaid debt:', err);
          return { data: null };
        }),
        api.get('/calendar-entries/manual-payment-dates', { params: requestParams }).catch(() => ({ data: { manualPaymentDates: [] } })),
        ...monthsToFetch.map(({ month, year }) =>
          api.get('/calendar-entries', { params: { month, year, ...requestParams } }).catch(() => ({ data: [] }))
        )
      ]);
      // API returns array directly or { payments: [...] }
      const jobsData = jobsResponse.data?.jobs || (Array.isArray(jobsResponse.data) ? jobsResponse.data : []);
      setJobs(jobsData);
      if (paymentsResponse._paymentsError) {
        setPayments([]);
        setPaymentsLoadError(true);
      } else {
        const paymentsList = Array.isArray(paymentsResponse.data)
          ? paymentsResponse.data
          : (paymentsResponse.data?.payments ?? []);
        setPayments(Array.isArray(paymentsList) ? paymentsList : []);
        setPaymentsLoadError(false);
      }
      const manualDatesRaw = manualDatesResponse?.data?.manualPaymentDateTimes
        || manualDatesResponse?.data?.manualPaymentDates
        || [];
      const fromTable = (manualDatesRaw || []).map((item) => {
        if (!item) return null;
        if (typeof item === 'string') {
          return { date: item.split('T')[0], time: '14:00' };
        }
        const rawDate = item.date || item.paymentDate || item.payment_date;
        if (!rawDate) return null;
        const normalizedDate = typeof rawDate === 'string'
          ? rawDate.split('T')[0]
          : rawDate instanceof Date
            ? rawDate.toISOString().split('T')[0]
            : null;
        if (!normalizedDate) return null;
        const rawTime = item.time || item.paymentTime || item.payment_time || '14:00';
        const cleanedTime = typeof rawTime === 'string' && /^\d{2}:\d{2}/.test(rawTime)
          ? rawTime.slice(0, 5)
          : '14:00';
        return { date: normalizedDate, time: cleanedTime };
      }).filter(Boolean);

      // Merge with manual payment dates from calendar entries (same source as Calendar page)
      const byDate = new Map();
      fromTable.forEach((item) => { byDate.set(item.date, item); });
      const allEntries = (calendarMonths || []).flatMap((r) => (Array.isArray(r?.data) ? r.data : []));
      allEntries.forEach((entry) => {
        const raw = entry?.manualPaymentDate ?? entry?.manual_payment_date;
        if (!raw) return;
        const dateStr = typeof raw === 'string'
          ? (raw.includes('T') ? raw.split('T')[0] : raw)
          : (raw instanceof Date ? raw.toISOString().split('T')[0] : null);
        if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
        if (!byDate.has(dateStr)) byDate.set(dateStr, { date: dateStr, time: '14:00' });
      });
      const normalizedManualDates = Array.from(byDate.values());
      setManualPaymentDates(normalizedManualDates);
      if (unpaidDebtResponse && unpaidDebtResponse.data) {
        setUnpaidDebt(unpaidDebtResponse.data);
      } else {
        // Fallback: calculate from jobs if unpaid-debt endpoint fails
        const jobsDebt = jobsData
          .filter(job => job.status === 'completed' || job.status === 'in_progress')
          .filter(job => job.paymentStatus === 'pending')
          .reduce((sum, job) => sum + (job.totalAmount || 0), 0);
        setUnpaidDebt({ totalDebt: jobsDebt });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildPaymentPayload = () => {
    const periodStart = normalizeDateToYmd(paymentForm.periodStartDate);
    const periodEnd = normalizeDateToYmd(paymentForm.periodEndDate);
    return {
      amount: parseFloat(paymentForm.amount),
      date: normalizeDateToYmd(paymentForm.date),
      method: paymentForm.method,
      description: paymentForm.description,
      status: paymentForm.status,
      periodStartDate: periodStart || null,
      periodEndDate: periodEnd || null,
      ...(effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {}),
    };
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const paymentData = buildPaymentPayload();
      if (!paymentData.date || !/^\d{4}-\d{2}-\d{2}$/.test(paymentData.date)) {
        showToast(
          (t('payments.addError') || 'Greška pri dodavanju plaćanja') + ': Neispravan datum plaćanja',
          'error'
        );
        return;
      }

      const response = await api.post('/payments', paymentData);
      if (response.data) {
        // Payment was saved successfully, reload data
        await loadData();
        setShowAddPayment(false);
        setEditingPayment(null);
        setMarkAsPaidTarget(null);
        resetForm();
        showToast(t('payments.addSuccess') || 'Plaćanje je uspešno dodato.', 'success');
      } else {
        showToast(t('payments.addError') || 'Greška pri dodavanju plaćanja: Nema odgovora sa servera.', 'error');
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      const errorMessage = formatApiError(error) || 'Nepoznata greška';
      const base = t('payments.addError') || 'Greška pri dodavanju plaćanja';
      showToast(`${base}: ${errorMessage}`, 'error');
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    
    // Safely extract date string
    let dateStr = '';
    if (payment.date) {
      if (typeof payment.date === 'string') {
        dateStr = payment.date.split('T')[0];
      } else if (payment.date instanceof Date) {
        dateStr = payment.date.toISOString().split('T')[0];
      }
    } else if (payment.createdAt) {
      if (typeof payment.createdAt === 'string') {
        dateStr = payment.createdAt.split('T')[0];
      } else if (payment.createdAt instanceof Date) {
        dateStr = payment.createdAt.toISOString().split('T')[0];
      }
    }
    if (!dateStr) {
      dateStr = new Date().toISOString().split('T')[0];
    }
    
    // Safely extract period dates
    let periodStartStr = '';
    let periodEndStr = '';
    if (payment.periodStartDate) {
      if (typeof payment.periodStartDate === 'string') {
        periodStartStr = payment.periodStartDate.split('T')[0];
      } else if (payment.periodStartDate instanceof Date) {
        periodStartStr = payment.periodStartDate.toISOString().split('T')[0];
      }
    }
    if (payment.periodEndDate) {
      if (typeof payment.periodEndDate === 'string') {
        periodEndStr = payment.periodEndDate.split('T')[0];
      } else if (payment.periodEndDate instanceof Date) {
        periodEndStr = payment.periodEndDate.toISOString().split('T')[0];
      }
    }
    
    setPaymentForm({
      amount: payment.amount ? payment.amount.toString() : '',
      date: dateStr,
      method: payment.method || 'bank_transfer',
      description: payment.description || '',
      status: payment.status || 'pending',
      periodStartDate: periodStartStr,
      periodEndDate: periodEndStr
    });
    setShowAddPayment(true);
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      const paymentData = buildPaymentPayload();

      const response = await api.put(`/payments/${editingPayment.id}`, paymentData);
      if (response.data) {
        // Payment was updated successfully, reload data
        await loadData();
        setShowAddPayment(false);
        setEditingPayment(null);
        setMarkAsPaidTarget(null);
        resetForm();
        showToast(t('payments.updateSuccess') || 'Plaćanje je uspešno ažurirano.', 'success');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      const errorMessage = formatApiError(error) || 'Nepoznata greška';
      const base = t('payments.updateError') || 'Greška pri ažuriranju plaćanja';
      showToast(`${base}: ${errorMessage}`, 'error');
    }
  };

  const handleDeletePayment = (paymentId) => {
    setDeleteConfirm({ isOpen: true, paymentId });
  };

  const confirmDeletePayment = async () => {
    const paymentId = deleteConfirm.paymentId;
    if (!paymentId) return;
    
    try {
      await api.delete(`/payments/${paymentId}`).catch(() => {
        setPayments(payments.filter(p => p.id !== paymentId));
      });
      loadData();
      showToast(t('payments.deleteSuccess') || 'Plaćanje je uspešno obrisano.', 'success');
    } catch (error) {
      showToast(t('payments.deleteError') || 'Greška pri brisanju plaćanja: ' + error.message, 'error');
    }
  };

  const handleMarkAsPaid = async (paymentId) => {
    const payment = payments.find((p) => p.id === paymentId);
    if (payment && payment.periodStartDate && payment.periodEndDate) {
      setEditingPayment(payment);
      setPaymentForm({
        amount: payment.amount?.toString() || '',
        date: new Date().toISOString().split('T')[0],
        method: payment.method || 'bank_transfer',
        description: payment.description || '',
        status: 'paid',
        periodStartDate: payment.periodStartDate,
        periodEndDate: payment.periodEndDate,
      });
      setShowAddPayment(true);
      return;
    }
    try {
      await api.put(`/payments/${paymentId}`, {
        status: 'paid',
        ...(effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {}),
      }).catch(() => {
        setPayments(payments.map(p =>
          p.id === paymentId ? { ...p, status: 'paid' } : p
        ));
      });
      loadData();
      showToast(t('payments.addSuccess') || 'Plaćanje je uspešno označeno.', 'success');
    } catch (error) {
      showToast(t('payments.markPaidError') || 'Greška pri označavanju kao plaćeno: ' + error.message, 'error');
    }
  };

  const openMarkAsPaidForDue = (month) => {
    const periodStart = month.dates?.[0] || unpaidDebt?.periodStartDate || '';
    const periodEnd = month.dates?.[month.dates.length - 1] || unpaidDebt?.periodEndDate || '';
    setPaymentForm({
      amount: (month.totalDebt || 0).toFixed(2),
      date: new Date().toISOString().split('T')[0],
      method: 'bank_transfer',
      description: '',
      status: 'paid',
      periodStartDate: periodStart,
      periodEndDate: periodEnd,
    });
    setEditingPayment(null);
    setMarkAsPaidTarget(month);
    setShowAddPayment(true);
  };

  const unpaidPastByMonth = useMemo(() => {
    if (!unpaidDebt?.unpaidEntries) {
      if (unpaidDebt?.totalDebt > 0 && unpaidDebt?.periodStartDate) {
        const start = normalizeDateToYmd(unpaidDebt.periodStartDate);
        const end = normalizeDateToYmd(unpaidDebt.periodEndDate) || start;
        if (start) {
          const d = new Date(`${start}T12:00:00`);
          const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          return [{
            monthName: `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`,
            monthKey,
            dates: (() => {
              const fromApi = (unpaidDebt.unpaidDates || []).map((x) => normalizeDateToYmd(x)).filter(Boolean);
              return fromApi.length > 0 ? fromApi : [start, ...(end !== start ? [end] : [])];
            })(),
            entries: [],
            totalDebt: unpaidDebt.totalDebt,
          }];
        }
      }
      return [];
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const months = {};
    Object.entries(unpaidDebt.unpaidEntries).forEach(([rawDateStr, entries]) => {
      const dateStr = normalizeDateToYmd(rawDateStr);
      if (!dateStr) return;
      const entryDate = new Date(`${dateStr}T12:00:00`);
      if (Number.isNaN(entryDate.getTime())) return;
      const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
      if (entryDateOnly > today) return;

      const entriesArray = Array.isArray(entries) ? entries : [entries];
      const monthKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}`;
      const monthName = `${MONTH_SHORT[entryDate.getMonth()]} ${entryDate.getFullYear()}`;

      if (!months[monthKey]) {
        months[monthKey] = {
          monthName,
          monthKey,
          dates: [],
          entries: [],
          totalDebt: 0,
        };
      }

      months[monthKey].dates.push(dateStr);
      months[monthKey].entries.push(...entriesArray);

      const defaultHourlyRate = unpaidDebt.defaultHourlyRate || 0;
      entriesArray.forEach((entry) => {
        const hours = getDurationHours(entry.startTime, entry.endTime);
        const rate = entry.hourlyRate || defaultHourlyRate;
        if (rate > 0 && hours > 0) {
          months[monthKey].totalDebt += hours * rate;
        }
      });
    });

    const sorted = Object.values(months).sort((a, b) => {
      const aDate = new Date(`${normalizeDateToYmd(a.dates[0])}T12:00:00`);
      const bDate = new Date(`${normalizeDateToYmd(b.dates[0])}T12:00:00`);
      return aDate - bDate;
    });

    if (sorted.length === 0 && unpaidDebt.totalDebt > 0 && unpaidDebt.periodStartDate) {
      const start = normalizeDateToYmd(unpaidDebt.periodStartDate);
      const end = normalizeDateToYmd(unpaidDebt.periodEndDate) || start;
      if (start) {
        const d = new Date(`${start}T12:00:00`);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return [{
          monthName: `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`,
          monthKey,
          dates: (() => {
            const fromApi = (unpaidDebt.unpaidDates || []).map((x) => normalizeDateToYmd(x)).filter(Boolean);
            return fromApi.length > 0 ? fromApi : [start, ...(end !== start ? [end] : [])];
          })(),
          entries: [],
          totalDebt: unpaidDebt.totalDebt,
        }];
      }
    }

    return sorted;
  }, [unpaidDebt]);

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getDate()).padStart(2, '0')}. ${MONTH_SHORT[d.getMonth()]}`;
  };

  const formatDateRange = (dates) => {
    if (!dates || dates.length === 0) return '';
    const sorted = [...dates].sort();
    return `${formatDateShort(sorted[0])} – ${formatDateShort(sorted[sorted.length - 1])}`;
  };

  const formatMonthYearLatin = (monthKey) => {
    if (!monthKey) return '';
    const [y, m] = monthKey.split('-').map(Number);
    const d = new Date(y, (m || 1) - 1, 1);
    return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  };

  const resetForm = (overrides = {}) => {
    const debt = overrides.unpaidDebt !== undefined ? overrides.unpaidDebt : unpaidDebt;
    const defaultPeriodStart = normalizeDateToYmd(debt?.periodStartDate) || '';
    const defaultPeriodEnd = normalizeDateToYmd(debt?.periodEndDate) || '';
    const defaultAmount = debt?.totalDebt ? Number(debt.totalDebt).toFixed(2) : '';

    setPaymentForm({
      amount: defaultAmount,
      date: new Date().toISOString().split('T')[0],
      method: 'bank_transfer',
      description: '',
      status: 'paid',
      periodStartDate: defaultPeriodStart,
      periodEndDate: defaultPeriodEnd,
    });
  };

  // Use unpaidDebt.totalDebt if available (includes calendar entries), otherwise fallback to jobs
  const currentCharge = unpaidDebt?.totalDebt !== undefined && unpaidDebt.totalDebt !== null
    ? unpaidDebt.totalDebt
    : jobs
        .filter(job => job.status === 'completed' || job.status === 'in_progress')
        .filter(job => job.paymentStatus === 'pending')
        .reduce((sum, job) => sum + (job.totalAmount || 0), 0);

  // Find babysitter friend
  const babysitterFriend = friends.find(f => f.relationshipType === 'babysitter');

  // Calculate next payment date from babysitter's payment schedule
  const calculateNextPaymentDate = () => {
    if (!babysitterFriend) {
      return null; // No babysitter friend
    }
    if (!babysitterFriend.paymentSchedule) {
      return null; // No payment schedule set
    }

    const paymentSchedule = babysitterFriend.paymentSchedule;
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset time to start of day
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    // Support new format (date + time + repeatType). Parse as local date (align with Calendar).
    if (paymentSchedule.date) {
      const raw = paymentSchedule.date;
      let scheduleDate;
      if (typeof raw === 'string') {
        const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
        const [y, m, d] = datePart.split('-').map(Number);
        if (y && m && d) scheduleDate = new Date(y, m - 1, d, 0, 0, 0, 0);
        else scheduleDate = new Date(raw);
      } else {
        scheduleDate = new Date(raw);
      }
      if (!scheduleDate || isNaN(scheduleDate.getTime())) return null;
      scheduleDate.setHours(0, 0, 0, 0);
      const scheduleDay = scheduleDate.getDate();
      
      // Calculate next occurrence based on repeatType
      if (paymentSchedule.repeatType === 'daily') {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
      } else if (paymentSchedule.repeatType === 'weekly') {
        const interval = parseInt(paymentSchedule.intervalWeeks, 10);
        const normalizedInterval = Number.isFinite(interval) && interval > 1 ? interval : 1;
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

        if (normalizedInterval > 1) {
          // Recurrence: scheduleDate + k * (interval * 7). Next = first in sequence >= today (same as calendar).
          const dayMs = 24 * 60 * 60 * 1000;
          const periodDays = normalizedInterval * 7;
          const daysBetween = (todayStart.getTime() - scheduleDate.getTime()) / dayMs;
          let k = Math.ceil(daysBetween / periodDays);
          const nextInSequence = new Date(scheduleDate.getTime());
          nextInSequence.setDate(nextInSequence.getDate() + k * periodDays);
          if (nextInSequence.getTime() < todayStart.getTime()) {
            nextInSequence.setDate(nextInSequence.getDate() + periodDays);
          }
          return nextInSequence;
        }

        const scheduleDayOfWeek = scheduleDate.getDay();
        const currentDayOfWeek = now.getDay();
        let daysUntilNext = scheduleDayOfWeek - currentDayOfWeek;
        if (daysUntilNext < 0) daysUntilNext += 7;
        const candidate = new Date(todayStart);
        candidate.setDate(candidate.getDate() + daysUntilNext);
        return candidate;
      } else if (paymentSchedule.repeatType === 'monthly') {
        // Next occurrence is same day of month
        if (currentDay < scheduleDay) {
          // This month - check if day exists in current month
          const lastDayOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
          const targetDay = Math.min(scheduleDay, lastDayOfCurrentMonth);
          return new Date(currentYear, currentMonth, targetDay);
        } else {
          // Next month
          const nextMonth = currentMonth + 1;
          const lastDayOfNextMonth = new Date(currentYear, nextMonth + 1, 0).getDate();
          const targetDay = Math.min(scheduleDay, lastDayOfNextMonth);
          return new Date(currentYear, nextMonth, targetDay);
        }
      }
    }

    // Support old format (backward compatibility)
    // Weekly scheduleType (days are weekdays)
    if (paymentSchedule.scheduleType === 'weekly' && Array.isArray(paymentSchedule.days)) {
      const weekdayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
      const validWeekdays = paymentSchedule.days
        .map(d => (typeof d === 'string' ? d.toLowerCase() : d))
        .filter(d => typeof d === 'string' && weekdayMap[d] !== undefined);
      if (validWeekdays.length > 0) {
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        let best = null;
        validWeekdays.forEach((wd) => {
          const targetDow = weekdayMap[wd];
          const diff = (targetDow - todayStart.getDay() + 7) % 7;
          const cand = new Date(todayStart);
          cand.setDate(cand.getDate() + diff);
          if (!best || cand < best) best = cand;
        });
        return best;
      }
    }

    const days = paymentSchedule.days || (paymentSchedule.dayOfMonth ? [paymentSchedule.dayOfMonth] : [1]);
    let targetDay = 1;
    if (days.includes('first')) {
      targetDay = 1;
    } else if (days.includes('last')) {
      targetDay = new Date(currentYear, currentMonth + 1, 0).getDate();
    } else if (days.length > 0) {
      const firstDay = days[0];
      if (typeof firstDay === 'number') {
        targetDay = firstDay;
      } else if (typeof firstDay === 'string' && !isNaN(parseInt(firstDay))) {
        targetDay = parseInt(firstDay);
      }
    }
    
    // Ensure targetDay is valid for the month
    const lastDayOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const validTargetDay = Math.min(targetDay, lastDayOfCurrentMonth);
    
    if (currentDay < validTargetDay) {
      return new Date(currentYear, currentMonth, validTargetDay);
    } else {
      // Next month
      const nextMonth = currentMonth + 1;
      const lastDayOfNextMonth = new Date(currentYear, nextMonth + 1, 0).getDate();
      const nextValidTargetDay = Math.min(targetDay, lastDayOfNextMonth);
      return new Date(currentYear, nextMonth, nextValidTargetDay);
    }
  };

  const nextPaymentInfo = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

    // Build manual candidates: same logic as Calendar (future or today with time not passed)
    const manualCandidates = (manualPaymentDates || [])
      .map((entry) => {
        if (!entry?.date) return null;
        const dateStr = typeof entry.date === 'string' && entry.date.includes('T') ? entry.date.split('T')[0] : entry.date;
        const parts = dateStr.split('-').map(Number);
        if (parts.length < 3 || parts.some(Number.isNaN)) return null;
        const [year, month, day] = parts;
        const timeStr = entry.time || '14:00';
        const [hours, mins] = timeStr.split(':').map((v) => parseInt(v, 10));
        const dateObj = new Date(year, month - 1, day, Number.isFinite(hours) ? hours : 14, Number.isFinite(mins) ? mins : 0, 0, 0);
        if (isNaN(dateObj.getTime())) return null;

        const paymentDateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);
        const isToday = paymentDateOnly.getTime() === todayStart.getTime();
        const isFuture = paymentDateOnly > todayStart;
        const timePassed = isToday && now.getHours() * 60 + now.getMinutes() >= (Number.isFinite(hours) ? hours : 14) * 60 + (Number.isFinite(mins) ? mins : 0);

        if (isFuture || (isToday && !timePassed)) {
          return { date: dateObj, source: 'manual', time: timeStr };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Schedule candidate (same formula as Calendar so "next" matches)
    let scheduleCandidate = null;
    let scheduleDate = calculateNextPaymentDate();
    const ps = babysitterFriend?.paymentSchedule;
    const scheduleTime = ps?.time || '14:00';
    const [hours, minutes] = scheduleTime.split(':').map((v) => parseInt(v, 10));

    // If schedule returned a date in the past or "today" with time already passed, advance to next occurrence
    if (scheduleDate && ps) {
      const scheduleDateOnly = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), scheduleDate.getDate(), 0, 0, 0, 0);
      const isToday = scheduleDateOnly.getTime() === todayStart.getTime();
      const timePassed = isToday && now.getHours() * 60 + now.getMinutes() >= (Number.isFinite(hours) ? hours : 14) * 60 + (Number.isFinite(minutes) ? minutes : 0);
      const shouldSkip = scheduleDateOnly < todayStart || (isToday && timePassed);

      if (shouldSkip && ps.date && ps.repeatType === 'weekly') {
        const interval = parseInt(String(ps.intervalWeeks || 1).trim(), 10);
        const periodDays = (Number.isFinite(interval) && interval > 1 ? interval : 1) * 7;
        const nextInSequence = new Date(scheduleDate.getTime());
        nextInSequence.setDate(nextInSequence.getDate() + periodDays);
        if (nextInSequence.getTime() >= todayStart.getTime()) scheduleDate = nextInSequence;
      } else if (shouldSkip && ps.date && ps.repeatType === 'monthly') {
        const nextMonth = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() + 1, scheduleDate.getDate(), 0, 0, 0, 0);
        if (nextMonth.getTime() >= todayStart.getTime()) scheduleDate = nextMonth;
      }
    }

    if (scheduleDate) {
      const dateWithTime = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), scheduleDate.getDate(), Number.isFinite(hours) ? hours : 14, Number.isFinite(minutes) ? minutes : 0, 0, 0);
      const scheduleDateOnly = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), scheduleDate.getDate(), 0, 0, 0, 0);
      const isToday = scheduleDateOnly.getTime() === todayStart.getTime();
      const timePassed = isToday && now.getHours() * 60 + now.getMinutes() >= (Number.isFinite(hours) ? hours : 14) * 60 + (Number.isFinite(minutes) ? minutes : 0);
      if (scheduleDateOnly >= todayStart && (!isToday || !timePassed)) {
        scheduleCandidate = { date: dateWithTime, source: 'schedule', time: scheduleTime || '14:00' };
      }
    }

    // Pick earliest (align with Calendar: manual + schedule, take minimum by date)
    const allCandidates = [...manualCandidates];
    if (scheduleCandidate) allCandidates.push(scheduleCandidate);
    if (allCandidates.length === 0) return null;
    allCandidates.sort((a, b) => a.date.getTime() - b.date.getTime());
    const chosen = allCandidates[0];
    // Debug: sledeći dan plaćanja (remove when fixed)
    if (typeof console !== 'undefined' && console.log) {
      console.log('[Payments] nextPayment debug', {
        manualPaymentDatesRaw: (manualPaymentDates || []).length,
        manualDates: (manualPaymentDates || []).map((e) => e?.date),
        manualCandidates: manualCandidates.map((c) => ({ date: c.date?.toISOString?.()?.slice(0, 10), source: c.source })),
        scheduleFromCalc: scheduleDate ? scheduleDate.toISOString?.()?.slice(0, 10) : null,
        scheduleCandidate: scheduleCandidate ? { date: scheduleCandidate.date?.toISOString?.()?.slice(0, 10), source: scheduleCandidate.source } : null,
        chosen: chosen ? { date: chosen.date?.toISOString?.()?.slice(0, 10), source: chosen.source } : null,
      });
    }
    return chosen;
  }, [babysitterFriend, manualPaymentDates]);

  const nextPayDay = nextPaymentInfo?.date || null;
  const nextPaymentTime = nextPaymentInfo?.time || null;

  // Calculate days until payment (date-only comparison)
  const daysUntilPayment = useMemo(() => {
    if (!nextPayDay) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paymentDate = new Date(nextPayDay);
    paymentDate.setHours(0, 0, 0, 0);
    const diffTime = paymentDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [nextPayDay]);

  const paymentsByMonth = payments.reduce((acc, payment) => {
    const raw = payment.date || payment.createdAt;
    if (!raw) return acc;
    const month = new Date(raw).toISOString().slice(0, 7);
    if (!acc[month]) acc[month] = [];
    acc[month].push(payment);
    return acc;
  }, {});

  // Sorted month keys (newest first) - for payment history sections
  const sortedMonthKeys = useMemo(() => {
    const keys = Object.keys(paymentsByMonth).sort((a, b) => b.localeCompare(a));
    return keys;
  }, [paymentsByMonth]);

  const visibleMonthKeys = sortedMonthKeys.slice(0, monthsToShow);
  const hasMoreMonths = sortedMonthKeys.length > monthsToShow;

  if (loading) {
    return (
        <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6B8E23]"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6 max-w-7xl mx-auto w-full space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <MdPayment className="text-2xl text-primary-600 dark:text-primary-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('payments.title') || 'Plaćanja'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Moderan pregled dugovanja, dospelih uplata i istorije plaćanja.
              </p>
            </div>
          </div>
          <button
            onClick={async () => {
              setEditingPayment(null);
              let freshDebt = unpaidDebt;
              try {
                const response = await api
                  .get('/payments/unpaid-debt', { params: sharedParams })
                  .catch(() => ({ data: null }));
                if (response?.data) {
                  freshDebt = response.data;
                  setUnpaidDebt(response.data);
                }
              } catch (error) {
                console.error('Error loading unpaid debt:', error);
              }
              resetForm({ unpaidDebt: freshDebt });
              setShowAddPayment(true);
            }}
            className="btn-primary flex items-center justify-center gap-2 w-full lg:w-auto"
          >
            <MdAdd />
            {t('payments.addPayment') || 'Dodaj plaćanje'}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/60 dark:border-gray-700/60 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('payments.currentCharge') || 'Trenutno dugovanje'}
            </p>
            <p className="text-3xl font-bold text-primary-600 mt-2">
              {formatCurrency(currentCharge)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('payments.updatedToday') || 'Ažurirano danas'}
            </p>
          </div>

          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/60 dark:border-gray-700/60 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('payments.nextPayDay') || 'Sledeći dan plaćanja'}
            </p>
            {nextPayDay && babysitterFriend ? (
              <>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-2">
                  {nextPayDay.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {nextPaymentTime ? ` u ${nextPaymentTime}` : ''}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('payments.toBabysitter') || 'Za'}: {babysitterFriend.name || '—'}
                </p>
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold">
                  {t('payments.inDays') || 'za'} {daysUntilPayment !== null ? daysUntilPayment : 0} {t('payments.days') || 'dana'}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {!babysitterFriend 
                  ? (t('payments.noBabysitter') || 'Nemate unetu dadilju. Dodajte je sa stranice Prijatelji.')
                  : (t('payments.noPaymentSchedule') || 'Nije podešena dinamika plaćanja.')
                }
              </p>
            )}
          </div>

          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/60 dark:border-gray-700/60 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('payments.dueSummary') || 'Dospelo neplaćeno'}
            </p>
            <p className="text-2xl font-bold text-amber-600 mt-2">
              {formatCurrency(unpaidPastByMonth.reduce((sum, m) => sum + (m.totalDebt || 0), 0))}
            </p>
            {unpaidPastByMonth.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {unpaidPastByMonth.slice(0, 3).map((month, idx) => (
                  <p key={`${month.monthName}-${idx}`} className="text-xs text-gray-600 dark:text-gray-400">
                    {month.monthName}: {formatDateRange(month.dates)} — {formatCurrency(month.totalDebt)}
                  </p>
                ))}
              </div>
            )}
            {unpaidPastByMonth.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('payments.noPastUnpaid') || 'Nema dospelih neplaćenih perioda.'}
              </p>
            )}
          </div>
        </div>

        <div className="w-full space-y-6">
          {/* Prvi deo dospeva — sledeće plaćanje koje dospeva */}
          {unpaidPastByMonth.length > 0 && (() => {
            const firstDue = unpaidPastByMonth[0];
            return (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/60 dark:border-gray-700/60 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <MdSchedule className="text-amber-500" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('payments.firstDueSection') || 'Prvi deo dospeva'}
                  </h2>
                </div>
                <div className="p-4 border border-amber-200 dark:border-amber-800 rounded-xl bg-amber-50/50 dark:bg-amber-900/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/30">
                        <MdReceipt className="text-xl text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {firstDue.monthName} — {formatDateRange(firstDue.dates)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('payments.unpaidPeriod') || 'Neplaćeni period'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(firstDue.totalDebt || 0)}
                      </p>
                      <button
                        onClick={() => openMarkAsPaidForDue(firstDue)}
                        className="mt-2 px-4 py-2 text-sm rounded-xl bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 font-medium"
                      >
                        {t('payments.markAsPaid') || 'Označi kao plaćeno'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Prethodna plaćanja — istorija */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/60 dark:border-gray-700/60 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <MdCalendarMonth className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('payments.previousPayments') || 'Prethodna plaćanja'}
              </h2>
            </div>

            {paymentsLoadError && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {t('payments.loadError') || 'Istorija plaćanja nije učitana. Pokušajte ponovo.'}
                </p>
                <button
                  type="button"
                  onClick={() => { setPaymentsLoadError(false); loadData(); }}
                  className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
                >
                  {t('payments.retry') || 'Ponovi učitavanje'}
                </button>
              </div>
            )}

            {visibleMonthKeys.length === 0 ? (
              <div className="text-center py-12">
                <MdReceipt className="text-5xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {t('payments.noPaymentsForMonth') || 'Nema plaćanja'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {visibleMonthKeys.map((monthKey) => {
                  const monthPayments = (paymentsByMonth[monthKey] || []).sort((a, b) => {
                    const da = new Date(a.date || a.createdAt || 0).getTime();
                    const db = new Date(b.date || b.createdAt || 0).getTime();
                    return db - da;
                  });
                  const monthTotal = monthPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                  const monthNameCap = formatMonthYearLatin(monthKey);
                  return (
                    <div key={monthKey} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{monthNameCap}</h3>
                        <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                          {t('payments.total') || 'Ukupno'}: {formatCurrency(monthTotal)}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {monthPayments.map((payment) => (
                  <div
                    key={payment.id}
                    className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 sm:p-2.5 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/90 dark:bg-gray-800/60 hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-sm transition-all"
                  >
                    <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                      payment.status === 'paid' ? 'bg-green-100/80 dark:bg-green-900/40' : 'bg-amber-100/80 dark:bg-amber-900/40'
                    }`}>
                      <MdReceipt className={`text-base ${payment.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                        {payment.description === 'Manual payment' ? t('payments.manualPayment') : (payment.description || t('payments.payment'))}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(payment.date || payment.createdAt).toLocaleDateString('sr-RS')}
                        {(payment.periodStartDate && payment.periodEndDate) && (
                          <span> • {new Date(payment.periodStartDate).toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' })} – {new Date(payment.periodEndDate).toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' })}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold ${
                        payment.status === 'paid'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      }`}>
                        {payment.status === 'paid' ? t('payments.paid') : (t('payments.pending') || 'Čeka')}
                      </span>
                      <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(parseFloat(payment.amount || 0))}
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditPayment(payment)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                          title={t('payments.edit') || 'Izmeni'}
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                          title={t('payments.delete') || 'Obriši'}
                        >
                          <MdDelete className="text-lg" />
                        </button>
                        {payment.status === 'pending' && (
                          <button
                            onClick={() => handleMarkAsPaid(payment.id)}
                            className="px-2 py-1 text-[11px] rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 font-medium"
                            title={t('payments.markAsPaid') || 'Označi kao plaćeno'}
                          >
                            {t('payments.markAsPaid') || 'Označi'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {hasMoreMonths && (
                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={() => setMonthsToShow((prev) => prev + 12)}
                      className="w-full py-3 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium"
                    >
                      Učitaj više
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Payment Modal */}
      {showAddPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg max-w-md w-full min-w-0 shadow-xl max-h-[100dvh] sm:max-h-[calc(100vh-2rem)] flex flex-col">
            <div className="px-6 pt-6 pb-2 min-w-0 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingPayment ? (t('payments.editPayment') || 'Edit Payment') : (t('payments.addPayment') || 'Add Payment')}
                </h3>
                <button
                  onClick={() => {
                    setShowAddPayment(false);
                    setEditingPayment(null);
                    setMarkAsPaidTarget(null);
                    resetForm();
                  }}
                  className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  ×
                </button>
              </div>
            </div>

            <form
              onSubmit={editingPayment ? handleUpdatePayment : handleAddPayment}
              className="flex-1 min-h-0 min-w-0 flex flex-col"
            >
              <div className="px-6 pb-4 space-y-4 min-w-0 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('payments.amount') || 'Amount (RSD)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('payments.paymentDate') || 'Datum plaćanja'}
                </label>
                <input
                  type="date"
                  value={normalizeDateToYmd(paymentForm.date)}
                  onChange={(e) => {
                    setPaymentForm({ ...paymentForm, date: e.target.value });
                  }}
                  className="payment-date-field w-full min-w-0 max-w-full box-border px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('payments.method') || 'Payment Method'}
                </label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="bank_transfer">{t('payments.bankTransfer') || 'Bank Transfer'}</option>
                  <option value="cash">{t('payments.cash') || 'Cash'}</option>
                  <option value="card">{t('payments.card') || 'Card'}</option>
                  <option value="other">{t('payments.other') || 'Other'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('payments.status') || 'Status'}
                </label>
                <select
                  value={paymentForm.status}
                  onChange={(e) => setPaymentForm({ ...paymentForm, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="paid">{t('payments.paid')}</option>
                  <option value="pending">{t('payments.pending') || 'Pending'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('payments.description') || 'Description'}
                </label>
                <input
                  type="text"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={t('payments.descriptionPlaceholder') || 'Optional description...'}
                />
              </div>

              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                  {t('payments.paymentPeriod') || 'Period plaćanja'}
                </label>
                
                {/* Show unpaid periods by month */}
                {unpaidPastByMonth.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                      {t('payments.unpaidPeriods') || 'Dospelo neplaćeno:'}
                    </p>
                    <div className="space-y-1">
                      {unpaidPastByMonth.map((month, idx) => (
                        <p key={idx} className="text-xs text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">{month.monthName}</span> {formatCurrency(month.totalDebt)} ({formatDateRange(month.dates)})
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {unpaidPastByMonth.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {t('payments.noPastUnpaid') || 'Nema dospelih neplaćenih perioda do danas.'}
                  </p>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                  <div className="min-w-0">
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                      {t('payments.periodStart') || 'Start Date'}
                    </label>
                    <input
                      type="date"
                      value={normalizeDateToYmd(paymentForm.periodStartDate)}
                      onChange={(e) => {
                        setPaymentForm({ ...paymentForm, periodStartDate: e.target.value });
                      }}
                      className="payment-date-field w-full min-w-0 max-w-full box-border px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                      {t('payments.periodEnd') || 'End Date'}
                    </label>
                    <input
                      type="date"
                      value={normalizeDateToYmd(paymentForm.periodEndDate)}
                      onChange={(e) => {
                        setPaymentForm({ ...paymentForm, periodEndDate: e.target.value });
                      }}
                      className="payment-date-field w-full min-w-0 max-w-full box-border px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              </div>

              <div className="flex gap-3 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] sm:pb-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPayment(false);
                    setEditingPayment(null);
                    setMarkAsPaidTarget(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, paymentId: null })}
        onConfirm={confirmDeletePayment}
        title={t('payments.deleteConfirmTitle') || 'Potvrda brisanja'}
        message={t('payments.deleteConfirm') || 'Da li ste sigurni da želite da obrišete ovo plaćanje?'}
        confirmText={t('common.delete') || 'Obriši'}
        cancelText={t('common.cancel') || 'Otkaži'}
        type="danger"
      />
    </div>
  );
}
